import type { ReactNode } from "react";
import { DisclosureChevron } from "@/components/common/Disclosure";
import { FileIcon } from "@/components/common/FileIcon";
import { Spinner } from "@/components/common/Spinner";
import {
	TREE_INDENT_PX,
	TREE_ROOT_PADDING_PX,
	TreeIndentGuides,
} from "@/components/file-tree/TreeIndentGuides";
import type { CloudSharedVolumeEntryPayload, EnvironmentEntryPayload } from "@/lib/dnd/payload";
import { useDraggable } from "@/lib/dnd/useDraggable";
import { useDropTarget } from "@/lib/dnd/useDropTarget";
import { getFileIconType } from "@/lib/file-type";
import { ROW_HOVER_CLASS, ROW_SELECTED_CLASS, TREE_ROW_DRAGOVER_CLASS } from "@/lib/row-styles";

/**
 * PC のファイルや ~ のもの、同じボリュームの行を落としたときの受け口。onDropHomeEntries が無ければ ~ のものは、
 * canMoveEntries が無ければ共有ボリュームの行は受けない。
 */
export interface SharedVolumeRowDrop {
	canDrop: boolean;
	onDropFiles: (dataTransfer: DataTransfer) => void;
	onDropHomeEntries?: (payload: EnvironmentEntryPayload) => void;
	canMoveEntries?: (payload: CloudSharedVolumeEntryPayload) => boolean;
	onMoveEntries?: (payload: CloudSharedVolumeEntryPayload) => void;
}

interface SharedVolumeTreeRowFrameProps {
	rowKey: string;
	name: string;
	depth: number;
	directory: boolean;
	expandable: boolean;
	expanded: boolean;
	/** ボリュームの根のときだけ、名前の横に添える環境の中のパス。 */
	mountPath: string | null;
	muted: boolean;
	deleted?: boolean;
	selected: boolean;
	/** Tab で入る行か。 */
	current: boolean;
	/** 直下を読んでいるか (spinnerTestId があれば、その印で出す)。 */
	spinner: boolean;
	spinnerTestId?: string;
	/** 行に出す説明 (使えない理由など)。あれば、名前とパスの全文の tooltip より優先する。 */
	title?: string;
	drop: SharedVolumeRowDrop | null;
	/** 行をドラッグして同じボリュームの中で移すときのペイロード。null ならドラッグさせない。 */
	dragPayload?: CloudSharedVolumeEntryPayload | null;
	/** 名前の後ろに並べる印。 */
	badges?: ReactNode;
	/** 行の右端のボタン。 */
	action?: ReactNode;
	onClick: (event: React.MouseEvent) => void;
	onContextMenu: (event: React.MouseEvent) => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

/** ファイルのツリーの共有ボリュームの 1 行の骨組み (シェブロン・アイコン・名前・根のパス・スピナー・落とす先)。 */
export function SharedVolumeTreeRowFrame({
	rowKey,
	name,
	depth,
	directory,
	expandable,
	expanded,
	mountPath,
	muted,
	deleted = false,
	selected,
	current,
	spinner,
	spinnerTestId,
	title,
	drop,
	dragPayload = null,
	badges,
	action,
	onClick,
	onContextMenu,
	onKeyDown,
}: SharedVolumeTreeRowFrameProps) {
	const canDrop = drop?.canDrop === true;
	const onDropHomeEntries = drop?.onDropHomeEntries;
	const dragProps = useDraggable(dragPayload);
	const { isOver, dropProps } = useDropTarget({
		accept: ["environment-entry", "cloud-shared-volume-entry"],
		canDrop: (payload) =>
			payload.kind === "environment-entry"
				? canDrop && onDropHomeEntries !== undefined
				: drop?.canMoveEntries?.(payload) === true,
		onDrop: (payload) => {
			if (payload.kind === "environment-entry") onDropHomeEntries?.(payload);
			else drop?.onMoveEntries?.(payload);
		},
		// ~ のものは動かさずにコピーする。
		payloadDropEffect: (payload) => (payload.kind === "environment-entry" ? "copy" : "move"),
		acceptFiles: {
			canDrop: () => canDrop,
			onDrop: (event) => drop?.onDropFiles(event.dataTransfer),
		},
	});

	return (
		<div
			{...dropProps}
			className={`relative flex min-w-0 items-center ${action ? "pr-1" : ""} ${
				isOver ? TREE_ROW_DRAGOVER_CLASS : selected ? ROW_SELECTED_CLASS : ROW_HOVER_CLASS
			}`}
		>
			<TreeIndentGuides depth={depth} />
			<button
				type="button"
				{...dragProps}
				onClick={(event) => {
					event.currentTarget.focus({ preventScroll: true });
					onClick(event);
				}}
				onContextMenu={(event) => {
					event.stopPropagation();
					onContextMenu(event);
				}}
				onKeyDown={onKeyDown}
				tabIndex={current ? 0 : -1}
				title={title}
				className="flex min-w-0 flex-1 cursor-pointer items-center gap-x-1.5 py-0.5 pr-2 text-left text-list focus-visible:outline focus-visible:outline-accent-solid focus-visible:[outline-offset:-1px]"
				style={{ paddingLeft: depth * TREE_INDENT_PX + TREE_ROOT_PADDING_PX }}
				data-testid={`shared-volume-node-${rowKey}`}
				data-row-key={rowKey}
				data-muted={muted ? "" : undefined}
			>
				{/* フォルダ名をファイル名と同じ位置に揃えるため、シェブロンもアイコンと同じ幅の枠に右寄せで置く。 */}
				<span className="flex w-4 shrink-0 justify-end">
					{directory ? (
						expandable && <DisclosureChevron open={expanded} size={12} />
					) : (
						<FileIcon type={getFileIconType(name)} muted={muted} />
					)}
				</span>
				<span
					className={`min-w-0 truncate ${mountPath === null ? "flex-1" : ""} ${muted || deleted ? "text-fg-4" : "text-fg-1"} ${directory ? "font-medium" : ""} ${deleted ? "line-through decoration-danger" : ""}`}
					title={title === undefined ? name : undefined}
					data-tooltip-if-truncated={title === undefined ? true : undefined}
					data-deleted={deleted ? "" : undefined}
				>
					{name}
				</span>
				{mountPath !== null && (
					<span
						className="min-w-0 flex-1 truncate text-caption text-fg-3"
						title={title === undefined ? mountPath : undefined}
						data-tooltip-if-truncated={title === undefined ? true : undefined}
						data-testid="shared-volume-root-path"
					>
						{mountPath}
					</span>
				)}
				{spinner && <Spinner data-testid={spinnerTestId} />}
				{badges}
			</button>
			{action}
		</div>
	);
}
